// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import authService from "../services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const data = await authService.fetchUserInfo();
    if (data) setUser(data);
    return data;
  }, []);

  useEffect(() => {
    const init = async () => {
      if (authService.isAuthenticated()) {
        await refreshUser();
      }
      setLoading(false);
    };
    init();
  }, [refreshUser]);

  const login = async (username, password) => {
    const result = await authService.signin(username, password);
    if (result.success) {
      setUser(authService.getUser());
    }
    return result;
  };

  const logout = () => {
    setUser(null);
    authService.logout();
  };

  // Check global permission
  const hasPermission = (codename) => {
    if (!user) return false;
    if (user.is_level_superuser) return true;
    return user.global_permissions?.includes(codename);
  };

  // Check team-scoped permission
  const hasTeamPermission = (codename, teamId) => {
    if (!user) return false;
    if (user.is_level_superuser) return true;
    // Check global first
    if (user.global_permissions?.includes(codename)) return true;
    // Check team-specific
    const tp = user.team_permissions?.[String(teamId)];
    return tp?.permissions?.includes(codename) || false;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    hasPermission,
    hasTeamPermission,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
